import { AnalyticsService } from 'base/analytics';
import { Button } from 'base/ui/button';
import { IconStars } from 'base/ui/icons';
import { Rows } from 'base/ui/layout';
import { Link } from 'base/ui/link';
import { PanelLayout } from 'base/ui/panel';
import { ToastQueue } from 'base/ui/toast';
import { TextRegular, TextSmallLight, TextTinyLight } from 'base/ui/typography';
import { BlocksService, FontFamilyMetadata } from 'blocks/blocks_service';
import { observer } from 'mobx-react-lite';
import React, { useEffect, useState } from 'react';
import { getGlobalConceptsStore } from './concept_store';
import { SidebarNavigation, TypographyCategory } from './routes';
import styles from './typography_onboarding_panel.css';
import { FontFamilyCard, TYPOGRAPHY_CATEGORIES } from './typography_panel';
import typographyPanelStyles from './typography_panel.css';

export const TypographyOnboardingPanel = observer(
  ({
    navigation,
    blocksService,
    analytics,
    toastQueue,
  }: {
    navigation: SidebarNavigation;
    blocksService: BlocksService;
    analytics: AnalyticsService;
    toastQueue: ToastQueue;
  }) => {
    const conceptsStore = getGlobalConceptsStore();
    const concept = conceptsStore.activeConcept;
    const hasLikedFonts = concept.getLikedFontFamilies('heading').length > 0;

    const [state, setState] = useState<{
      selectedCategory: TypographyCategory | null;
      searchResults?: FontFamilyMetadata[];
    } | null>(null);

    const handleFinishOnboarding = () => {
      concept.setFontOnboardingCompleted(true);
    };

    useEffect(() => {
      if (state?.selectedCategory === undefined) return;

      const controller = new AbortController();

      if (state.selectedCategory !== null) {
        blocksService
          .searchFontFamilies('', [state.selectedCategory], controller.signal)
          .then(({ results }) => {
            setState({
              selectedCategory: state.selectedCategory,
              searchResults: results.slice(0, 4),
            });
          });
      } else {
        // Fetch the top result from each category.
        Promise.all(
          TYPOGRAPHY_CATEGORIES.map(({ slug }) =>
            blocksService.searchFontFamilies('', [slug], controller.signal),
          ),
        ).then((allResults) => {
          setState({
            selectedCategory: null,
            searchResults: allResults.map(({ results }) => results[0]),
          });
        });
      }
    }, [blocksService, state?.selectedCategory]);

    return (
      <PanelLayout
        title={state == null ? 'Headings' : ''}
        navigation={{
          back: state !== null ? () => setState(null) : undefined,
          close: navigation.close,
        }}
        footer={
          <div className={styles.footer}>
            {state === null ? (
              <Button
                variant="secondary"
                label="Skip font matching"
                onClick={handleFinishOnboarding}
              />
            ) : (
              <>
                <Button
                  variant="primary"
                  background="ai"
                  icon={IconStars}
                  label="Find recommendations"
                  disabled={!hasLikedFonts}
                  onClick={handleFinishOnboarding}
                />
                <TextTinyLight color="grey" align="center">
                  Like 1 font for recommendations.{' '}
                  <Link onClick={handleFinishOnboarding}>Skip</Link>
                </TextTinyLight>
              </>
            )}
          </div>
        }
      >
        <div className={styles.page}>
          <div className={styles.instructions}>
            <div className={styles.progress}>
              <div
                className={
                  state === null
                    ? styles.progressInner1
                    : !hasLikedFonts
                    ? styles.progressInner2
                    : styles.progressInner3
                }
              />
            </div>
            <div className={styles.title}>
              <TextRegular align="center">
                {state === null
                  ? 'Pick a style for this concept'
                  : state.selectedCategory === null
                  ? 'Like a font to continue'
                  : `Like a font from '${
                      TYPOGRAPHY_CATEGORIES.find(
                        ({ slug }) => slug === state.selectedCategory,
                      )?.label
                    }'`}
              </TextRegular>
              <TextSmallLight align="center" color="grey">
                {state === null
                  ? 'Help us understand the direction you are going for this concept'
                  : 'AI will use this font to recommend you other similar fonts'}
              </TextSmallLight>
            </div>
          </div>
          {state === null ? (
            <div className={styles.categoryButtons}>
              {TYPOGRAPHY_CATEGORIES.map(({ slug, label, image }) => (
                <Button
                  variant="secondary"
                  key={slug}
                  onClick={() => setState({ selectedCategory: slug })}
                >
                  <div className={styles.categoryButton}>
                    <img src={image} />
                  </div>
                </Button>
              ))}
              <Button
                variant="secondary"
                label="Not sure"
                onClick={() => setState({ selectedCategory: null })}
              />
            </div>
          ) : (
            <Rows spacing={8}>
              {state.searchResults?.map((fontFamily, i) => (
                <FontFamilyCard
                  key={fontFamily.slug}
                  fontFamily={fontFamily}
                  analytics={analytics}
                  category="onboarding"
                  toastQueue={toastQueue}
                  target="heading"
                  footerText={
                    state?.selectedCategory === null
                      ? TYPOGRAPHY_CATEGORIES[i]?.label
                      : ''
                  }
                  showLikeCallout={i === 1}
                />
              )) ??
                new Array(4).fill(null).map((_, i) => (
                  <div
                    className={typographyPanelStyles.fontCardSkeleton}
                    key={i}
                    style={{
                      animationDelay: `${i * 0.1 - 1}s`,
                    }}
                  />
                ))}
            </Rows>
          )}
        </div>
      </PanelLayout>
    );
  },
);
